import type { RootState } from '@/store';
import { alpha, Card, CardContent, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';

import type { FeeItem } from '@/apis/extended/admin/interestRateApi';
import {
  computeMergedFeeBreakdown,
  type CalculatedFeeRule,
} from '@/utils/fees/feeEngine';
import { ProductEsitmation } from '@/types/ProductType';
import { StackedFeeItem } from './StackedFeeItem';

type ProductDetailSubtotalClosingCostsProps = {
  estimation: ProductEsitmation;
};

export const ProductDetailSubtotalClosingCosts = ({
  estimation,
}: ProductDetailSubtotalClosingCostsProps) => {
  const theme = useTheme();
  const feeItemsByLoanType = useSelector(
    (state: RootState) => (state.interestRate as any).feeItemsByLoanType
  ) as Partial<Record<string, FeeItem[]>>;
  const calculatedFeeRulesByLoanType = useSelector(
    (state: RootState) =>
      (state.interestRate as any).calculatedFeeRulesByLoanType
  ) as Partial<Record<string, CalculatedFeeRule[]>>;

  if (!estimation) return null;

  const loanTypeMap: Record<string, string> = {
    conventional: 'Conventional',
    fha: 'FHA',
    va: 'VA',
    usda: 'USDA',
    dscr: 'DSCR',
    commercial: 'Commercial',
    fix_and_flip: 'FixAndFlip',
    bridge: 'Bridge',
    heloc: 'HELOC',
    heloan: 'HELOAN',
    second_mortgage: 'SecondMortgage',
    cash: 'Cash',
    default: 'Conventional',
  };
  const loanType =
    loanTypeMap[((estimation as { type?: string }).type || '').toLowerCase()] ||
    'Conventional';
  const feeItems = feeItemsByLoanType?.[loanType] || [];
  const calculatedFeeRules = calculatedFeeRulesByLoanType?.[loanType] || [];
  const breakdown = computeMergedFeeBreakdown({
    estimation,
    calculatedFeeRules,
    feeItems,
    loanAmount: (estimation as { loanAmount?: number }).loanAmount || 0,
  });

  return (
    <Card
      sx={{
        background: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.primary,
        borderRadius: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <StackedFeeItem
          label="Total Closing Costs"
          value={breakdown.totalClosingCosts}
        />
        <StackedFeeItem label="Down Payment" value={estimation.downPayment} />
        <StackedFeeItem
          label="Total Cash to Close"
          value={breakdown.totalClosingCosts + estimation.downPayment}
        />
      </CardContent>
    </Card>
  );
};
